import { Trophy, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useCountUp } from '../../hooks/useCountUp';
import { useJackpotEvents, type JackpotEvent } from '../../hooks/useJackpotEvents';
import {
  jackpotService,
  type JackpotGameView,
  type JackpotView,
} from '../../service/jackpot';
import { formatMoney, formatRelativeTime } from '../../utils/format';

const OPEN_KEY = 'jackpot-widget-open';
const POS_KEY = 'jackpot-widget-pos';
/** Nhịp làm tươi "nổ X trước" (giờ/phút trôi) — 30s là đủ mượt, không tốn. */
const RELATIVE_TICK_MS = 30_000;
/** Thời gian giữ hiệu ứng flash sau khi một hũ nổ. */
const FLASH_MS = 1_600;

/**
 * Màu + icon chip theo game. Màu nền theo convention SUN
 * (`GroupCard.GAME_BADGE_COLORS`): TX xanh dương, XĐ tím, TL cam. Icon dùng đúng
 * emoji tin báo hũ Telegram (`telegram.monitor.ts`): TX 🎲, TL 🀄, XĐ 🟤.
 */
const STYLE: Record<'tx' | 'xd' | 'tl', { text: string; chip: string; label: string }> = {
  tx: { text: 'text-[#3b82f6]', chip: 'bg-[#3b82f6] text-white', label: '🎲' },
  xd: { text: 'text-[#8b5cf6]', chip: 'bg-[#8b5cf6] text-white', label: '🟤' },
  tl: { text: 'text-[#f59e0b]', chip: 'bg-[#f59e0b] text-white', label: '🀄' },
};
const FALLBACK_STYLE = { text: 'text-[#64748b]', chip: 'bg-[#64748b] text-white', label: '🎰' };
const styleFor = (game: string) => STYLE[game as 'tx' | 'xd' | 'tl'] ?? FALLBACK_STYLE;

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) !== 'false';
  } catch {
    return true;
  }
}
/** Kích thước sticker (để đặt mặc định ở góc + kẹp trong màn hình). */
const STICKER_SIZE = 56;
const EDGE_MARGIN = 8;

function readPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw) as { x: number; y: number };
  } catch {
    // bỏ qua — dùng mặc định
  }
  // Mặc định: góc dưới-phải (chỗ quen thuộc của nút nổi).
  return {
    x: window.innerWidth - STICKER_SIZE - 20,
    y: window.innerHeight - STICKER_SIZE - 20,
  };
}

/** Kẹp toạ độ (top-left) để phần tử `w×h` nằm gọn trong màn hình. */
function clampIntoView(px: number, py: number, w: number, h: number): { x: number; y: number } {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - w - EDGE_MARGIN);
  const maxY = Math.max(EDGE_MARGIN, window.innerHeight - h - EDGE_MARGIN);
  return {
    x: Math.min(Math.max(EDGE_MARGIN, px), maxX),
    y: Math.min(Math.max(EDGE_MARGIN, py), maxY),
  };
}

/** Số hũ, chạy mượt từ giá trị cũ → mới. */
function Pot({ value, className }: { value: number; className: string }) {
  const shown = useCountUp(value, 700);
  return (
    <span className={className}>
      {formatMoney(shown)} <span className="text-[9px] tracking-wider text-[#475569]">VND</span>
    </span>
  );
}

/**
 * Widget báo hũ nổi: sticker tròn (ghim góc) → popup kéo-thả hiển thị hũ 3 game
 * realtime + "nổ bao lâu trước". Mount 1 lần trong layout (mọi trang đã đăng
 * nhập). Ẩn hoàn toàn khi jackpot-client chưa đọc được giá trị (API trả
 * `games: []`).
 */
export function JackpotWidget() {
  const [view, setView] = useState<JackpotView | null>(null);
  const [open, setOpen] = useState(readOpen);
  const [flashGame, setFlashGame] = useState<string | null>(null);
  const [, forceTick] = useState(0);

  // Vị trí popup (top-left tuyệt đối, áp bằng CSS left/top). Nhớ qua localStorage.
  const [pos, setPos] = useState(readPos);
  const popupRef = useRef<HTMLDivElement>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragCleanup = useRef<(() => void) | null>(null);

  // Có hũ để HIỆN không (điều khiển widget hiện/ẩn + vòng làm tươi giờ).
  const active = (view?.games.length ?? 0) > 0;

  // Ảnh chụp ban đầu (mở app). Lỗi/chưa có dữ liệu → view rỗng → widget ẩn.
  useEffect(() => {
    let cancelled = false;
    void jackpotService
      .getState()
      .then((data) => {
        if (!cancelled) setView(data);
      })
      .catch(() => {
        if (!cancelled) setView({ status: 'disconnected', updatedAt: null, games: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Realtime: `state` cập nhật giá trị (giữ lastHitAt cũ); `win` set mốc nổ + flash.
  //
  // SSE LUÔN mở (không gate theo `active`): nếu ảnh chụp REST đầu rỗng vì
  // jackpot-client kết nối chậm hơn lúc user mở web, ta vẫn cần nghe để hiện
  // widget lên NGAY khi `state` đầu tiên tới — không bắt user reload.
  useJackpotEvents((event: JackpotEvent) => {
    if (event.type === 'state') {
      setView((prev) => mergeState(prev, event.games, event.status, event.updatedAt));
    } else {
      setView((prev) => applyWin(prev, event.game, event.value, event.at));
      setFlashGame(event.game);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlashGame(null), FLASH_MS);
    }
  });

  // Làm tươi "nổ X trước" theo thời gian trôi — chỉ khi widget đang hiện.
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => forceTick((n) => n + 1), RELATIVE_TICK_MS);
    return () => clearInterval(timer);
  }, [active]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, String(open));
    } catch {
      // bỏ qua
    }
  }, [open]);

  // Dọn khi unmount: timer flash + listener kéo còn treo (unmount giữa lúc đang kéo).
  useEffect(() => {
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
      dragCleanup.current?.();
    };
  }, []);

  const savePos = (p: { x: number; y: number }): void => {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(p));
    } catch {
      // bỏ qua
    }
  };

  // Kéo popup bằng pointer THỦ CÔNG. Chỉ khởi động từ header; kẹp trong màn hình;
  // lưu localStorage khi thả.
  const startDrag = (e: React.PointerEvent): void => {
    const el = popupRef.current;
    const w = el?.offsetWidth ?? 400;
    const h = el?.offsetHeight ?? 300;
    const offX = e.clientX - pos.x;
    const offY = e.clientY - pos.y;
    let latest = pos;

    const move = (ev: PointerEvent): void => {
      latest = clampIntoView(ev.clientX - offX, ev.clientY - offY, w, h);
      setPos(latest);
    };
    const end = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      dragCleanup.current = null;
      savePos(latest);
    };
    const stop = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      dragCleanup.current = null;
    };
    dragCleanup.current = stop; // để effect unmount gỡ nếu đang kéo dở
    window.addEventListener('pointermove', move);
    // touch/pen có thể phát `pointercancel` thay `pointerup` (gesture/OS ngắt) →
    // nghe cả hai, nếu không listener treo làm kéo "kẹt" + leak.
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  // Khi MỞ: popup 400px có thể tràn nếu vị trí cũ sát mép — kẹp lại cho gọn.
  useLayoutEffect(() => {
    if (!open) return;
    const el = popupRef.current;
    if (!el) return;
    const c = clampIntoView(pos.x, pos.y, el.offsetWidth, el.offsetHeight);
    setPos(c);
    savePos(c);
    // Chỉ kẹp khi MỞ; cố ý KHÔNG phụ thuộc `pos` để không chạy lại mỗi lần kéo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const games = view?.games ?? [];
  if (games.length === 0) return null; // chưa có dữ liệu → ẩn hẳn

  // ── thu gọn: sticker (GHIM cố định góc phải, không kéo) ──
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Mở báo hũ"
        className="fixed bottom-5 right-5 z-[60] grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#f59e0b] to-yellow-600 text-yellow-950 shadow-lg shadow-yellow-500/30 transition-transform hover:scale-105"
      >
        <Trophy size={24} />
        <span className="absolute right-1 top-1 h-3 w-3 rounded-full border-2 border-[#0d0f14] bg-[#ef4444]" />
      </button>
    );
  }

  // ── mở: popup kéo-thả (pointer thủ công) ──
  return (
    <div
      ref={popupRef}
      style={{ left: pos.x, top: pos.y }}
      className="fixed z-[60] w-[400px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-[#252a38] bg-[#161920] shadow-2xl"
    >
      {/* Header = tay cầm kéo (chỉ kéo popup từ đây, không phải cả thân) */}
      <div
        onPointerDown={startDrag}
        className="flex cursor-grab touch-none items-center gap-2 border-b border-[#252a38] bg-gradient-to-r from-[#f59e0b]/10 to-transparent px-4 py-3 active:cursor-grabbing"
      >
        <span className="h-2 w-2 animate-pulse rounded-full bg-[#22c55e]" />
        <span className="text-sm font-bold tracking-wide text-[#e2e8f0]">JACKPOT LIVE</span>
        <span className="ml-auto select-none text-xs tracking-widest text-[#475569]">⠿</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Đóng, thu về sticker"
          className="grid h-6 w-6 place-items-center rounded-md border border-[#252a38] bg-[#10131a] text-[#64748b] hover:bg-[#ef4444] hover:text-white"
        >
          <X size={14} />
        </button>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-1.5 p-2">
        {games.map((g) => (
          <JackpotRow key={g.game} game={g} flash={flashGame === g.game} />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-[#252a35] px-4 py-2 text-[10px] text-[#475569]">
        <span>cập nhật {view?.updatedAt ? formatRelativeTime(view.updatedAt) : '—'}</span>
        <span className="font-semibold tracking-wide text-[#22c55e]">● LIVE</span>
      </div>
    </div>
  );
}

function JackpotRow({ game, flash }: { game: JackpotGameView; flash: boolean }) {
  const s = styleFor(game.game);
  return (
    <div
      className={`relative grid grid-cols-[40px_1fr_auto] items-center gap-3 rounded-xl border bg-[#10131a] px-3 py-2.5 transition-colors duration-500 ${
        flash ? 'border-[#f59e0b] bg-[#f59e0b]/10' : 'border-[#252a35]'
      }`}
    >
      <div className={`grid h-10 w-10 place-items-center rounded-lg text-lg font-bold ${s.chip}`}>
        {s.label}
      </div>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-[#e2e8f0]">{game.name}</div>
        <div
          className="truncate font-mono text-[11px] text-[#475569]"
          title={game.lastHitAt ? new Date(game.lastHitAt).toLocaleString('vi-VN') : undefined}
        >
          {game.lastHitAt ? (
            <>
              nổ{' '}
              {game.lastValue !== undefined && (
                <span className="font-semibold text-[#f59e0b]">{formatMoney(game.lastValue)}đ</span>
              )}{' '}
              · <span className="font-semibold text-[#64748b]">{formatRelativeTime(game.lastHitAt)}</span>
            </>
          ) : (
            'chưa ghi nhận nổ'
          )}
        </div>
      </div>
      <Pot
        value={game.jackpot}
        className={`text-right font-mono text-[14px] font-bold tabular-nums ${s.text}`}
      />
    </div>
  );
}

// ── merge helpers (thuần) ──

/** Cập nhật giá trị hũ từ event `state`, GIỮ `lastHitAt`/`lastValue` đã biết. */
function mergeState(
  prev: JackpotView | null,
  games: { game: 'tx' | 'xd' | 'tl'; name?: string; jackpot: number }[],
  status: JackpotView['status'],
  updatedAt: string,
): JackpotView {
  const prevByGame = new Map((prev?.games ?? []).map((g) => [g.game, g]));
  return {
    status,
    updatedAt,
    games: games.map((g) => {
      const old = prevByGame.get(g.game);
      return {
        game: g.game,
        name: g.name ?? old?.name ?? g.game.toUpperCase(),
        jackpot: g.jackpot,
        ...(old?.lastHitAt ? { lastHitAt: old.lastHitAt, lastValue: old.lastValue } : {}),
      };
    }),
  };
}

/** Ghi mốc nổ mới cho một hũ (từ event `win`). */
function applyWin(
  prev: JackpotView | null,
  game: 'tx' | 'xd' | 'tl',
  value: number,
  at: number,
): JackpotView | null {
  if (!prev) return prev;
  return {
    ...prev,
    games: prev.games.map((g) =>
      g.game === game ? { ...g, lastHitAt: new Date(at).toISOString(), lastValue: value } : g,
    ),
  };
}
